import React, { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faRobot } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UI_COLORS } from '../utils/colors';
import { useWarehouseContext } from '../context/WarehouseContext';
import aiAdvisorService from '../services/aiAdvisorService';
import MessageAlert from '../components/common/MessageAlert';
import SearchableSelect from '../components/common/SearchableSelect';

/**
 * Purchase Plan — generates an AI buying plan from the business's own recent
 * anonymised sales/purchase trends. Each generation runs in its own thread so
 * it stays available for follow-up questions in AI Chat. Advisory only.
 */
const AIPurchasePlanPage = () => {
  const { t } = useTranslation();
  const strings = t('aiAdvisor', { returnObjects: true });
  const { selectedWarehouseIds } = useWarehouseContext();

  const [windowWeeks, setWindowWeeks] = useState(8);
  const [categories, setCategories] = useState([]); // Sub Categories for the scope picker
  const [catalogueIds, setCatalogueIds] = useState([]); // selected Sub Category ids; [] = all
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [result, setResult] = useState(null); // { plan, as_of, products_analysed, window_weeks }
  const [alert, setAlert] = useState(null);

  // Load Sub Categories for the scope picker (non-fatal if it fails)
  useEffect(() => {
    let active = true;
    (async () => {
      const res = await aiAdvisorService.getCategories();
      if (active && res.success) setCategories(res.data?.categories || []);
    })();
    return () => { active = false; };
  }, []);

  const showError = useCallback((res) => {
    if (res?.status === 503) {
      setDisabled(true);
      return;
    }
    setAlert({ type: 'error', title: strings.plan.title, message: res?.error || strings.errors.generic });
  }, []);

  const handleGenerate = async () => {
    if (loading) return;
    setLoading(true);
    setResult(null);

    // A plan must live in a thread; create a fresh one each time.
    const created = await aiAdvisorService.createSession(`Purchase plan (last ${windowWeeks} weeks)`);
    if (!created.success) {
      setLoading(false);
      return showError(created);
    }

    const res = await aiAdvisorService.plan(created.data.id, {
      window_weeks: windowWeeks,
      warehouse_ids: selectedWarehouseIds,
      catalogue_ids: catalogueIds,
    });
    setLoading(false);

    if (!res.success) return showError(res);
    const data = res.data;
    if (data?.data_available === false) {
      setAlert({ type: 'info', title: strings.plan.title, message: data.plan || strings.plan.noData });
      return;
    }
    setResult(data);
  };

  if (disabled) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <FontAwesomeIcon icon={faRobot} className="text-4xl mb-3 text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">{strings.plan.title}</h2>
          <p className="text-gray-600">{strings.errors.notEnabled}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
      {alert && (
        <div className="fixed top-4 right-4 z-50">
          <MessageAlert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />
        </div>
      )}

      <div className="mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FontAwesomeIcon icon={faClipboardList} /> {strings.plan.title}
        </h1>
        <p className="text-sm text-gray-500">{strings.plan.subtitle}</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3 sm:gap-4">
          <div className="shrink-0">
            <label className="block text-xs font-medium text-gray-500 mb-1">{strings.plan.windowWeeks}</label>
            <input
              type="number"
              min={1}
              max={52}
              value={windowWeeks}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setWindowWeeks(Number.isNaN(v) ? 8 : Math.min(52, Math.max(1, v)));
              }}
              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
          {categories.length > 0 && (
            <div className="flex-1 basis-[180px] min-w-0 sm:max-w-[300px]">
              <label className="block text-xs font-medium text-gray-500 mb-1">{strings.plan.subCategory}</label>
              <SearchableSelect
                name="catalogue_ids"
                isMulti
                value={catalogueIds}
                onChange={(e) => setCatalogueIds(e.target.value || [])}
                options={categories.map((c) => ({ value: c.id, label: c.name }))}
                placeholder={strings.plan.subCategoryPlaceholder}
              />
            </div>
          )}
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full sm:w-auto justify-center px-5 py-2 rounded-xl text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: UI_COLORS.NAVIGATION?.ACTIVE || '#1f2937' }}
          >
            <FontAwesomeIcon icon={faClipboardList} />
            {loading ? strings.plan.generating : strings.plan.generate}
          </button>
        </div>
        <p className="text-[11px] text-gray-400 mt-3">{strings.plan.advisoryNote}</p>
      </div>

      {/* Result */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5 min-h-[200px]">
        {loading ? (
          <p className="text-sm text-gray-500 italic">{strings.plan.generating}</p>
        ) : result ? (
          <>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500 mb-3 pb-3 border-b border-gray-100">
              {result.as_of && (
                <span>{strings.plan.asOf}: <strong className="text-gray-700">{result.as_of}</strong></span>
              )}
              {result.window_weeks != null && (
                <span>{strings.plan.windowWeeks}: <strong className="text-gray-700">{result.window_weeks}</strong></span>
              )}
              {result.products_analysed != null && (
                <span>{strings.plan.productsAnalysed}: <strong className="text-gray-700">{result.products_analysed}</strong></span>
              )}
            </div>
            <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{result.plan}</div>
          </>
        ) : (
          <p className="text-gray-400 text-sm">{strings.plan.empty}</p>
        )}
      </div>

      <p className="text-[11px] text-gray-400 mt-3">{strings.chat.privacyNote}</p>
    </div>
  );
};

export default AIPurchasePlanPage;
